import { useCallback, useMemo, useState } from "react";

interface DateInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label: string;
  placeholder?: string;
}

// Months for display
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Check if a string looks like a fuzzy/approximate date
function isFuzzyDate(text: string | null): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  // Fuzzy date indicators: abt, about, circa, bef, aft, bet, ~, ?, etc.
  const fuzzyPatterns = [
    /^abt\.?\s/i,
    /^about\s/i,
    /^circa\s/i,
    /^ca\.?\s/i,
    /^bef\.?\s/i,
    /^before\s/i,
    /^aft\.?\s/i,
    /^after\s/i,
    /^bet\.?\s/i,
    /^between\s/i,
    /^~\s?/,
    /\?/,
    /^\d{4}$/,          // Just a year
    /^[a-z]{3,9}\s+\d{4}$/i, // Just month and year
  ];
  return fuzzyPatterns.some(pattern => pattern.test(lower));
}

// Try to parse a date string into a Date object
function parseDate(text: string | null): Date | null {
  if (!text) return null;
  
  // Try DD MMM YYYY format first (e.g., "15 Jun 1950")
  const ddMmmYyyy = text.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
  if (ddMmmYyyy) {
    const day = parseInt(ddMmmYyyy[1], 10);
    const monthStr = ddMmmYyyy[2].substring(0, 3);
    const monthIndex = MONTHS.findIndex(m => m.toLowerCase() === monthStr.toLowerCase());
    const year = parseInt(ddMmmYyyy[3], 10);
    if (monthIndex >= 0 && day >= 1 && day <= 31 && year >= 1 && year <= 9999) {
      return new Date(year, monthIndex, day);
    }
  }
  
  // Try ISO format (YYYY-MM-DD)
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    return new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3], 10));
  }
  
  // Try native Date parsing as fallback
  const parsed = new Date(text);
  if (!isNaN(parsed.getTime())) {
    return parsed;
  }
  
  return null;
}

// Format a Date object to DD MMM YYYY
function formatDate(date: Date): string {
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

// Convert Date to ISO format for input[type="date"]
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function DateInput({ value, onChange, label, placeholder }: DateInputProps) {
  // Determine initial mode based on whether value looks like a fuzzy date
  const [isTextMode, setIsTextMode] = useState(() => isFuzzyDate(value));
  
  // Parse current value for date picker
  const parsedDate = useMemo(() => parseDate(value), [value]);
  const isoValue = parsedDate ? toIsoDate(parsedDate) : "";
  
  // Handle date picker change
  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isoValue = e.target.value;
    if (!isoValue) {
      onChange(null);
      return;
    }
    const [year, month, day] = isoValue.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    onChange(formatDate(date));
  }, [onChange]);
  
  // Handle text input change
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    onChange(text || null);
  }, [onChange]);
  
  // Toggle between modes
  const toggleMode = useCallback(() => {
    setIsTextMode(prev => !prev);
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="block text-sm text-vault-600">{label}</label>
        <button
          type="button"
          onClick={toggleMode}
          className="text-xs text-vault-500 hover:text-vault-700 underline"
          title={isTextMode ? "Switch to date picker" : "Switch to text input for approximate dates"}
        >
          {isTextMode ? "Use date picker" : "Enter text"}
        </button>
      </div>
      
      {isTextMode ? (
        <input
          type="text"
          value={value || ""}
          onChange={handleTextChange}
          placeholder={placeholder || "e.g., abt 1850, Jun 1920, 15 Jun 1950"}
          className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
        />
      ) : (
        <div className="mt-1 flex items-center gap-2">
          <input
            type="date"
            value={isoValue}
            onChange={handleDateChange}
            className="flex-1 rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
          />
          {value && (
            <span className="text-sm text-vault-500" title="Stored as">
              → {value}
            </span>
          )}
        </div>
      )}
      
      <p className="mt-1 text-xs text-vault-400">
        {isTextMode 
          ? "For approximate dates (abt, before, after) or partial dates (year only)"
          : "Select exact date - stored as DD MMM YYYY"
        }
      </p>
    </div>
  );
}
